/*
 * Copyright (c) 2024-2026, s0up and the autobrr contributors.
 * SPDX-License-Identifier: GPL-2.0-or-later
 */

import React from "react";
import { motion } from "motion/react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { cn } from "@/lib/utils";

export type WidgetAccent = "ok" | "warn" | "error" | "neutral";

// The Card's base className already sets a shorthand `border` color that
// expands to border-left-color too, so a plain `border-l-{color}` can lose
// the cascade depending on Tailwind's generated rule order. The `!` marks
// these as important to guarantee the accent always wins.
const ACCENT_BORDER: Record<WidgetAccent, string> = {
  ok: "border-l-4 !border-l-emerald-500",
  warn: "border-l-4 !border-l-amber-500",
  error: "border-l-4 !border-l-red-500",
  neutral: "border-l-4 !border-l-transparent",
};

interface WidgetCardProps {
  title: string;
  accent?: WidgetAccent;
  children: React.ReactNode;
}

// Shared chrome for every kiosk widget so they all read as one system --
// individual widgets only need to render their own content and (optionally)
// a status accent color.
export const WidgetCard: React.FC<WidgetCardProps> = ({
  title,
  accent = "neutral",
  children,
}) => (
  <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} className="h-full">
    <Card className={cn("h-full", ACCENT_BORDER[accent])}>
      <CardHeader className="py-2 px-3">
        <CardTitle className="text-sm">{title}</CardTitle>
      </CardHeader>
      <CardContent className="px-3 pb-3">{children}</CardContent>
    </Card>
  </motion.div>
);
